/* StripeSyncPanel — shown above the tier cards in Admin → Pricing.
 *
 * Why it exists: editing a tier's price in Admin updates the DB and the
 * marketing pages instantly, but Stripe Prices are immutable — checkout
 * still charges the OLD `stripe_price_id` until a new Price is issued.
 * This panel surfaces the drift and provides a one-click "Update Stripe
 * price" action that creates the new Price + archives the old one.
 *
 * Three top-level states:
 *   • Loading      → spinner
 *   • Stripe off   → friendly hint to go configure Stripe under Settings
 *   • Loaded       → per-tier badge + action button when there's drift
 */
import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { RefreshCw, Check, AlertTriangle, Link2Off, Zap, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_META = {
  synced:     { label: 'Synced',       icon: Check,        cls: 'bg-emerald-100 text-emerald-700' },
  mismatch:   { label: 'Price drift',  icon: AlertTriangle, cls: 'bg-amber-100 text-amber-700' },
  not_linked: { label: 'Not linked',   icon: Link2Off,     cls: 'bg-zinc-100 text-zinc-700' },
  inactive:   { label: 'Archived',     icon: AlertTriangle, cls: 'bg-rose-100 text-rose-700' },
  error:      { label: 'Lookup error', icon: AlertTriangle, cls: 'bg-rose-100 text-rose-700' },
};

const fmt = (cents) => (cents == null ? '—' : `$${(cents / 100).toFixed(2)}`);

export const StripeSyncPanel = ({ api }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [syncingId, setSyncingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/tiers/stripe-sync-status');
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to check Stripe sync');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const handleSync = async (tier) => {
    setSyncingId(tier.tier_id);
    try {
      await api.post(`/admin/tiers/${tier.tier_id}/sync-stripe`);
      toast.success(`${tier.name} synced to Stripe`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncingId(null);
    }
  };

  return (
    <Card className="border-zinc-200" data-testid="stripe-sync-panel">
      <CardHeader className="pb-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <Zap className="w-4 h-4 text-indigo-600" />
              Stripe sync
            </CardTitle>
            <CardDescription className="text-xs mt-1">
              Verify each plan's price in the database matches the live Stripe Price used at checkout.
              Stripe Prices are immutable — if the DB price drifts, click <strong>Update Stripe price</strong> to
              issue a new Price and archive the old one. Existing subscribers keep their original rate until renewal.
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={loading}
            data-testid="stripe-sync-recheck-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Re-check
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {loading ? (
          <div className="flex items-center justify-center py-6 text-zinc-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
            Checking Stripe…
          </div>
        ) : !data ? (
          <p className="text-sm text-zinc-500">Unable to load Stripe status.</p>
        ) : !data.stripe_configured ? (
          <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
            <AlertTriangle className="w-4 h-4 inline-block mr-1.5 -mt-0.5" />
            Stripe isn't configured yet. Add your secret key under <strong>Settings → Stripe</strong>,
            then re-check to verify each plan's price.
          </div>
        ) : data.tiers.length === 0 ? (
          <p className="text-sm text-zinc-500">No paid plans to sync.</p>
        ) : (
          <div className="space-y-2">
            {data.tiers.map((t) => {
              const meta = STATUS_META[t.status] || STATUS_META.error;
              const Icon = meta.icon;
              const driftAmount =
                t.status === 'mismatch'
                  ? `${fmt(t.stripe_amount)} on Stripe vs $${t.db_price.toFixed(2)} in app`
                  : null;
              const showAction = t.status === 'mismatch' || t.status === 'inactive' || t.status === 'not_linked';
              return (
                <div
                  key={t.tier_id}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 rounded-lg border border-zinc-200 bg-white"
                  data-testid={`stripe-sync-row-${t.tier_id}`}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-zinc-900">{t.name}</span>
                      <Badge
                        className={`${meta.cls} text-xs gap-1 hover:opacity-90`}
                        data-testid={`stripe-sync-status-${t.tier_id}`}
                      >
                        <Icon className="w-3 h-3" />
                        {meta.label}
                      </Badge>
                      <span className="text-xs text-zinc-500 font-mono">
                        ${t.db_price.toFixed(2)}/mo in app
                      </span>
                    </div>
                    {driftAmount && (
                      <p className="text-xs text-amber-700 mt-1">{driftAmount}</p>
                    )}
                    {t.status === 'not_linked' && (
                      <p className="text-xs text-zinc-500 mt-1">
                        No Stripe Price linked yet — auto-created on the first checkout, or click to pre-create now.
                      </p>
                    )}
                    {t.status === 'error' && t.error && (
                      <p className="text-xs text-rose-700 mt-1 truncate" title={t.error}>{t.error}</p>
                    )}
                  </div>
                  {showAction && (
                    <Button
                      variant={t.status === 'mismatch' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => handleSync(t)}
                      disabled={syncingId === t.tier_id}
                      className={t.status === 'mismatch' ? 'bg-amber-600 hover:bg-amber-700' : ''}
                      data-testid={`stripe-sync-action-${t.tier_id}`}
                    >
                      {syncingId === t.tier_id ? (
                        <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                      ) : (
                        <Zap className="w-3.5 h-3.5 mr-1.5" />
                      )}
                      {t.status === 'not_linked' ? 'Create Stripe price' : 'Update Stripe price'}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
